import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Loader2, Sparkles, RefreshCw, Check, Plus } from 'lucide-react';
import Portal from './Portal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// §5 Stage 2 — Coworker debrief panel. Lazy: GET the cached debrief; if none,
// offer to generate it (POST). Renders 4 sections; needs-from-user items are
// pre-filled editable mini-forms that save straight to the fact bank (§4 CRUD).

const humanize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Required fields per fact type — mirrors backend FACT_TYPE_REQUIRED (models.py), so the
// mini-form always renders what the backend needs, even if draft_fields omitted them.
const REQUIRED_FIELDS = {
  result: ['metric', 'after'],
  pricing: ['package_name', 'price'],
  testimonial: ['quote'],
  process: ['method_name'],
  observation: ['claim'],
};

const NeedCard = ({ need, voiceId, articleId, index, getAuthHeaders, onSaved }) => {
  const factType = need.suggested_fact_type || 'observation';
  const required = REQUIRED_FIELDS[factType] || [];
  const buildInitial = () => {
    const draft = (need.draft_fields && typeof need.draft_fields === 'object') ? need.draft_fields : {};
    const out = {};
    required.forEach((k) => { out[k] = draft[k] || ''; });         // always render required fields
    Object.entries(draft).forEach(([k, v]) => { if (!(k in out)) out[k] = v; });  // + any extras
    if (Object.keys(out).length === 0) out.claim = '';
    return out;
  };
  const [fields, setFields] = useState(buildInitial);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(() => !!need.answered);   // persisted answered-state, survives reopen
  const [error, setError] = useState('');
  const [match, setMatch] = useState(null);   // existing fact this save would duplicate
  const savingRef = useRef(false);            // in-flight guard: block a rapid second click before `saving` commits

  // Best-effort: record which fact answered this need so the panel shows Saved ✓ on reopen.
  const resolveNeed = async (factId) => {
    if (articleId == null || index == null || !factId) return;
    try {
      await axios.post(`${API}/articles/${articleId}/debrief/needs/${index}/resolve`,
        { fact_id: factId }, { headers: getAuthHeaders() });
    } catch { /* the fact is already saved; answered-state is non-critical */ }
  };

  // mode: 'auto' probes for a duplicate first; 'update' PUTs the matched fact; 'new' forces a POST.
  const save = async (mode = 'auto') => {
    if (savingRef.current) return;
    if (!voiceId) { setError('Create a brand voice first to save facts.'); return; }
    savingRef.current = true; setSaving(true); setError('');
    try {
      const cleaned = Object.fromEntries(
        Object.entries(fields).filter(([, v]) => String(v).trim())
      );
      if (mode === 'auto') {
        const probe = await axios.post(`${API}/brand/voices/${voiceId}/facts/match`,
          { fact_type: factType, fields: cleaned }, { headers: getAuthHeaders() });
        if (probe.data?.match) { setMatch(probe.data.match); return; }   // let the user choose update vs new
      }
      let factId;
      if (mode === 'update' && match) {
        await axios.put(`${API}/brand/facts/${match.id}`,
          { fields: cleaned }, { headers: getAuthHeaders() });
        factId = match.id;
      } else {
        const res = await axios.post(`${API}/brand/voices/${voiceId}/facts`,
          { fact_type: factType, fields: cleaned }, { headers: getAuthHeaders() });
        factId = res.data?.id;
      }
      await resolveNeed(factId);
      setSaved(true);
      onSaved && onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save fact');
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="card p-4 bg-[var(--color-surface)] flex items-center gap-2 text-sm" style={{ boxShadow: 'none' }}>
        <Check className="w-4 h-4 text-green-600 flex-shrink-0" /> Saved to your fact bank ✓
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <p className="text-sm">{need.ask}</p>
      {Object.keys(fields).length > 0 && (
        <div className="space-y-2">
          {Object.entries(fields).map(([k, v]) => (
            <div key={k}>
              <label className="block text-[10px] font-medium text-[var(--color-muted)] mb-1">{humanize(k)}{required.includes(k) ? ' *' : ''}</label>
              <input type="text" value={v} onChange={(e) => setFields((prev) => ({ ...prev, [k]: e.target.value }))}
                className="input text-sm" data-testid={`need-field-${k}`} />
            </div>
          ))}
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
      {match ? (
        <div className="space-y-2" data-testid="dedup-choice">
          <p className="text-xs text-[var(--color-muted)]">
            This looks like an existing fact: <span className="font-medium text-[var(--color-charcoal)]">{match.display_text}</span>
          </p>
          <div className="flex gap-2">
            <button onClick={() => save('update')} disabled={saving} className="btn btn-secondary text-xs" data-testid="dedup-update-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Update existing
            </button>
            <button onClick={() => save('new')} disabled={saving} className="btn btn-ghost text-xs" data-testid="dedup-new-btn">
              Save as new
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => save('auto')} disabled={saving} className="btn btn-secondary text-xs" data-testid="save-as-fact-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Save as fact
        </button>
      )}
    </div>
  );
};

const DebriefPanel = ({ articleId, voiceId, autoGenerate, getAuthHeaders, onClose, onFactSaved }) => {
  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(true);   // initial GET
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const autoTriggered = useRef(false);

  const generate = useCallback(async (refresh = false) => {
    setGenerating(true); setError('');
    try {
      const res = await axios.post(`${API}/articles/${articleId}/debrief${refresh ? '?refresh=true' : ''}`,
        {}, { headers: getAuthHeaders() });
      setDebrief(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not generate debrief');
    } finally {
      setGenerating(false);
    }
  }, [articleId, getAuthHeaders]);

  // Lazy load: GET the cached debrief. If none exists AND this is a fresh generation,
  // auto-trigger once (the GET-first + ref guard ensure never more than once / article).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/articles/${articleId}/debrief`, { headers: getAuthHeaders() });
        if (!cancelled) setDebrief(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          if (autoGenerate && !autoTriggered.current) {
            autoTriggered.current = true;
            if (!cancelled) generate(false);
          }
        } else if (!cancelled) {
          setError('Could not load debrief');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [articleId, getAuthHeaders, autoGenerate, generate]);

  // write-back target: the voice the debrief grounded against, else the article's voice
  const targetVoiceId = debrief?.voice_id || voiceId;

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="debrief-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-[var(--color-bg)] h-full overflow-y-auto shadow-xl p-6 border-l-4 border-[var(--color-coral)]">
        {/* Accent header — this is the differentiator, not a settings drawer */}
        <div className="flex items-center justify-between mb-6 -mx-6 -mt-6 px-6 py-4 bg-[var(--color-coral)]/10 border-b border-[var(--color-coral)]/30">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--color-coral)]/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-[var(--color-coral)]" />
            </div>
            <div>
              <h2 className="font-serif text-xl leading-tight">Coworker debrief</h2>
              <p className="text-[10px] text-[var(--color-muted)]">What Stilos did — and what it needs from you</p>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-5 h-5 animate-spin text-[var(--color-muted)]" /></div>
        ) : !debrief ? (
          <div className="text-center py-10">
            <p className="text-sm text-[var(--color-muted)] mb-5">
              See which brand facts Stilos used, why it made its structural calls, and what it needs from you.
            </p>
            <button onClick={() => generate(false)} disabled={generating} className="btn btn-primary" data-testid="get-debrief-btn">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</> : <><Sparkles className="w-4 h-4" /> Get the debrief</>}
            </button>
            {error && <p className="text-xs text-red-500 mt-3">{error}</p>}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Facts used */}
            <section data-testid="debrief-facts-used">
              <h3 className="font-display text-sm font-medium mb-2">Facts I used ({debrief.facts_used.length})</h3>
              {debrief.facts_used.length ? (
                <div className="flex flex-wrap gap-2">
                  {debrief.facts_used.map((f, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--color-surface)] text-[var(--color-charcoal)]" title={f.evidence_quote}>
                      {f.display_text}
                    </span>
                  ))}
                </div>
              ) : <p className="text-xs text-[var(--color-muted)]">None of your facts landed in this draft.</p>}
            </section>

            {/* Facts missing */}
            {debrief.facts_missing.length > 0 && (
              <section data-testid="debrief-facts-missing">
                <h3 className="font-display text-sm font-medium mb-2">Facts I didn't use ({debrief.facts_missing.length})</h3>
                <p className="text-[10px] text-[var(--color-muted)] mb-2">You have these assets but they didn't make this draft — steer a rewrite to weave them in.</p>
                <div className="flex flex-wrap gap-2">
                  {debrief.facts_missing.map((f, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--color-border)] text-[var(--color-muted)]">
                      {f.display_text}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Structural decisions */}
            {debrief.structural_decisions.length > 0 && (
              <section data-testid="debrief-structural">
                <h3 className="font-display text-sm font-medium mb-2">Why I structured it this way</h3>
                <ul className="space-y-2">
                  {debrief.structural_decisions.map((d, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-medium text-[var(--color-charcoal)]">{d.decision}</span>
                      <span className="text-[var(--color-muted)]"> — {d.why}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Needs from user */}
            {debrief.needs_from_user.length > 0 && (
              <section data-testid="debrief-needs">
                <h3 className="font-display text-sm font-medium mb-2">What I need from you</h3>
                {!targetVoiceId && (
                  <p className="text-[10px] text-[var(--color-muted)] mb-2">Attach a brand voice to save these as reusable facts.</p>
                )}
                <div className="space-y-3">
                  {debrief.needs_from_user.map((n, i) => (
                    <NeedCard key={i} index={i} need={n} voiceId={targetVoiceId} articleId={articleId} getAuthHeaders={getAuthHeaders} onSaved={onFactSaved} />
                  ))}
                </div>
              </section>
            )}

            <button onClick={() => generate(true)} disabled={generating}
              className="btn btn-ghost text-xs w-full" data-testid="refresh-debrief-btn">
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Regenerate debrief
            </button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
};

export default DebriefPanel;
